<!-- ─── Shared header template ─────────────────────────────────────────────── -->
<ng-template #chatHeader>
    <div class="rise-sidebar-header">
        <div class="rise-sidebar-title">
            <app-rise-agent-icon variant="light" [size]="34"></app-rise-agent-icon>
            <div class="rise-title-text">
                <span class="rise-name">RISE</span>
                <span class="beta-badge">Beta</span>
                <!-- <span class="rise-subtitle">Real-time Intelligence for Safety Excellence</span> -->
            </div>
        </div>
        <div class="rise-header-actions">
            <button
                class="new-chat-btn"
                pTooltip="New conversation"
                tooltipPosition="bottom"
                (click)="startNewChat()">
                <i class="pi pi-plus"></i>
                <span>New</span>
            </button>
            <p-button
                [text]="true"
                [pTooltip]="displayMode === 'pinned' ? 'Undock' : 'Dock to side'"
                tooltipPosition="bottom"
                (click)="togglePin()">
                <app-custom-icon
                    iconPath="assets/icons/general-icons.svg"
                    [imageName]="displayMode == 'pinned' ?'icon-undock-sidebar' :'icon-dock-sidebar'"
                    imageSize="24">
                </app-custom-icon>
            </p-button>
            <button
                class="close-chat-btn"
                pTooltip="Minimize"
                tooltipPosition="bottom"
                (click)="onClose()">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.93569 15.9235C8.53185 16.3273 8.53185 16.9821 8.93569 17.3859C9.33952 17.7898 9.99426 17.7898 10.3981 17.3859L15.0343 12.7498L15.0485 12.7374L15.0609 12.725C15.4648 12.3211 15.4648 11.6664 15.0609 11.2625L10.4123 6.61391C10.0085 6.21008 9.35374 6.21008 8.9499 6.61391C8.54607 7.01774 8.54607 7.67249 8.9499 8.07632L12.8667 11.993L8.93569 15.9235Z"/>
                </svg>                
            </button>
        </div>
    </div>
</ng-template>

<!-- ─── Shared body template ───────────────────────────────────────────────── -->
<ng-template #chatBody>
    <div class="rise-chat-layout">

        <!-- Message list -->
        <div class="messages-area" #messagesContainer>
            <ng-container *ngFor="let msg of messages">

                <!-- Trial notice (standalone full-width alert) -->
                <div
                    *ngIf="msg.type === 'trial-notice'"
                    class="trial-notice-wrapper"
                    [class.trial-notice--exhausted]="msg.trialExhausted">
                    <i
                        class="pi trial-notice-icon"
                        [class.pi-info-circle]="!msg.trialExhausted"
                        [class.pi-exclamation-triangle]="msg.trialExhausted"></i>
                    <div class="trial-notice-content">
                        <span>{{ msg.content }}</span>
                        <a
                            *ngIf="msg.trialExhausted"
                            [href]="subscriptionUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="trial-notice-button">
                            Upgrade RISE
                        </a>
                    </div>
                </div>

                <!-- Recommended reading card (resource links) -->
                <div *ngIf="msg.type === 'resource-links'" class="rise-card resource-links-card">
                    <div class="rise-card-header">
                        <i class="pi pi-book rise-card-icon"></i>
                        <span class="rise-card-title">Recommended Reading</span>
                    </div>
                    <ul class="rise-card-list">
                        <li *ngFor="let link of msg.resourceLinks" class="rise-card-item">
                            <a [href]="link.url" target="_blank" rel="noopener noreferrer" class="rise-card-link">
                                {{ link.title }}
                            </a>
                            <span *ngIf="link.summary" class="rise-card-meta">{{ link.summary }}</span>
                        </li>
                    </ul>
                </div>

                <!-- Download prompt card -->
                <div *ngIf="msg.type === 'download-prompt'" class="rise-card download-prompt-card">
                    <div class="download-prompt-body">
                        <span class="download-prompt-text">Would you like to download this as a PDF report?</span>
                        <button
                            class="download-prompt-btn"
                            type="button"
                            [disabled]="isLoading"
                            (click)="downloadReport()">
                            <i class="pi pi-download"></i>
                            Download PDF
                        </button>
                    </div>
                </div>

                <!-- Related items card (record links) -->
                <div *ngIf="msg.type === 'record-links'" class="rise-card record-links-card">
                    <div class="rise-card-header">
                        <i class="pi pi-list rise-card-icon"></i>
                        <span class="rise-card-title">Related Items</span>
                    </div>
                    <ul class="rise-card-list">
                        <li *ngFor="let link of msg.recordLinks" class="rise-card-item">
                            <a
                                *ngIf="link.url; else noLinkTpl"
                                class="rise-card-link"
                                (click)="onRecordLinkClick(link.url)">
                                {{ link.title }}
                            </a>
                            <ng-template #noLinkTpl>
                                <span class="rise-card-link rise-card-link--plain">{{ link.title }}</span>
                            </ng-template>
                            <span *ngIf="link.type || link.status" class="rise-card-meta">
                                <span *ngIf="link.type" class="rise-card-badge">{{ getRecordTypeLabel(link.type) }}</span>
                                <span *ngIf="link.status" class="rise-card-badge" [ngClass]="getBadgeStatusClass(link.status)">{{ link.status }}</span>
                            </span>
                        </li>
                    </ul>
                </div>

                <!-- Upgrade suggestion card -->
                <div *ngIf="msg.type === 'upgrade-suggestion'" class="upgrade-suggestion-card">
                    <div class="upgrade-suggestion-body">
                        <p class="upgrade-suggestion-text">{{ msg.content }}</p>
                        <a
                            [href]="subscriptionUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="upgrade-suggestion-btn">
                            Upgrade now
                        </a>
                    </div>
                </div>

                <!-- Chart card -->
                <div *ngIf="msg.type === 'chart'" class="rise-card p-1 px-3">
                    <app-rise-agent-chart [chartData]="msg.chartData"></app-rise-agent-chart>
                </div>

                <!-- Privacy notice card -->
                <div *ngIf="msg.type === 'privacy-notice'" class="privacy-notice">
                    <div class="privacy-notice-content">
                        <i class="pi pi-lock" style="flex-shrink: 0; font-size: 0.9rem;"></i>
                        <div>
                            <strong>Your analysis is private</strong>
                            <p>All conversations and safety data analysis are specific to your account. Your data is never shared with other organizations or used to train AI models.</p>
                        </div>
                    </div>
                </div>

                <!-- Regular chat bubble -->
                <div
                    *ngIf="msg.type !== 'trial-notice' && msg.type !== 'resource-links' && msg.type !== 'record-links' && msg.type !== 'download-prompt' && msg.type !== 'upgrade-suggestion' && msg.type !== 'chart' && msg.type !== 'privacy-notice'"
                    class="message-wrapper"
                    [class.user-wrapper]="msg.role === 'user'"
                    [class.assistant-wrapper]="msg.role === 'assistant'">

                    <div
                        class="message-bubble"
                        [class.user-bubble]="msg.role === 'user'"
                        [class.assistant-bubble]="msg.role === 'assistant'"
                        [class.failure-bubble]="msg.success === false">

                        <i
                            *ngIf="msg.success === false"
                            class="pi pi-exclamation-circle failure-icon"
                            aria-hidden="true"></i>

                        <div class="bubble-text" [innerHTML]="msg.content | markdown"></div>

                        <!-- Upgrade prompt (owner only) -->
                        <div *ngIf="msg.requiresUpgrade && msg.showOwnerUpgrade" class="upgrade-prompt">
                            <i class="pi pi-lock upgrade-icon"></i>
                            <div class="upgrade-content">
                                <p class="upgrade-text">This request requires an upgrade to the paid version of RISE Agent.</p>
                                <a
                                    [href]="subscriptionUrl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="upgrade-link">
                                    View subscription options →
                                </a>
                                <p *ngIf="msg.showOwnerUpgrade" class="upgrade-owner-note">
                                    As the account owner, you can enable RISE from your billing settings.
                                </p>
                            </div>
                        </div>

                        <!-- Suggested actions -->
                        <div *ngIf="msg.suggestedActions?.length" class="suggested-actions">
                            <button
                                *ngFor="let action of msg.suggestedActions"
                                class="suggested-action-chip"
                                type="button"
                                [disabled]="isLoading"
                                (click)="sendSuggestedAction(action)">
                                {{ action }}
                            </button>
                        </div>

                    </div>
                </div>

            </ng-container>

            <!-- Quick start chips -->
            <div *ngIf="showQuickStartChips" class="quick-start-chips">
                <button
                    *ngFor="let chip of quickStartChips"
                    class="quick-start-chip"
                    type="button"
                    [disabled]="isLoading"
                    (click)="onQuickStartChipClick(chip)">
                    {{ chip }}
                </button>
            </div>

            <!-- Typing indicator -->
            <div *ngIf="isLoading" class="typing-indicator-wrapper">
                <div class="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>
        </div>

        <!-- Context indicator -->
        <div *ngIf="currentRecordLabel" class="context-indicator">
            <i class="pi pi-eye"></i> Viewing: {{ currentRecordLabel }}
        </div>

        <!-- Input area -->
        <div class="input-area">
            <div class="textarea-wrapper">
                <textarea
                    class="rise-textarea"
                    [formControl]="messageControl"
                    placeholder="Ask RISE anything…"
                    rows="3"
                    [attr.disabled]="isLoading ? true : null"
                    (keydown)="onEnterKey($event)">
                </textarea>
                <div class="input-footer">
                    <span class="input-hint">Enter to send · Shift+Enter for new line</span>
                    <button
                        class="send-button"
                        type="button"
                        [disabled]="isLoading || !messageControl.value.trim()"
                        (click)="sendMessage()">
                        <i class="pi pi-arrow-up"></i>
                    </button>
                </div>
            </div>
            <p class="input-disclaimer">
                <i class="pi pi-lock"></i>
                Private and secure. Always verify safety decisions with qualified personnel.
            </p>
        </div>

    </div>
</ng-template>

<!-- ─── Overlay mode (PrimeNG sidebar) ────────────────────────────────────── -->
<p-sidebar
    *ngIf="displayMode === 'overlay'"
    [visible]="_visible"
    position="right"
    styleClass="min-w-screen lg:min-w-0 w-5 flex flex-column"
    [showCloseIcon]="false"
    (onHide)="onSidebarHide()">

    <ng-template pTemplate="header">
        <ng-container *ngTemplateOutlet="chatHeader"></ng-container>
    </ng-template>

    <ng-container *ngTemplateOutlet="chatBody"></ng-container>

</p-sidebar>

<!-- ─── Pinned mode (flex panel) ──────────────────────────────────────────── -->
<div *ngIf="displayMode === 'pinned'" class="rise-pinned-panel">
    <div class="rise-pinned-header">
        <ng-container *ngTemplateOutlet="chatHeader"></ng-container>
    </div>
    <ng-container *ngTemplateOutlet="chatBody"></ng-container>
</div>

<!-- ─── Trial confirmation dialog ─────────────────────────────────────────── -->
<div *ngIf="showTrialConfirm" class="trial-confirm-backdrop" (click)="cancelTrialRequest()">
    <div class="trial-confirm-dialog" (click)="$event.stopPropagation()">
        <div class="trial-confirm-icon">
            <i class="pi pi-info-circle"></i>
        </div>
        <p class="trial-confirm-text">{{ trialConfirmText }}</p>
        <div class="trial-confirm-actions">
            <button class="trial-confirm-btn trial-confirm-btn--cancel" type="button" (click)="cancelTrialRequest()">
                Cancel
            </button>
            <button class="trial-confirm-btn trial-confirm-btn--continue" type="button" (click)="confirmTrialRequest()">
                Continue
            </button>
        </div>
    </div>
</div>
